import axios from 'axios'

import { i18n } from '@/i18n'
import { clearAuthStorage, getToken } from '@/utils/authStorage'

/**
 * 业务错误。
 *
 * 后端失败时返回的是 `{ success: false, code, message }`，其中 code 是稳定的错误码
 * 字符串。把它包成一个异常抛出来，调用方就能用熟悉的 try/catch 处理，
 * 而不用在每个 then 里判断 res.success。
 */
export class ApiError extends Error {
  /**
   * @param {string} code        后端错误码，如 AUTH_BAD_CREDENTIALS
   * @param {string} rawMessage  后端返回的开发者可读说明，仅用于排障
   * @param {number} [httpStatus] HTTP 状态码
   */
  constructor(code, rawMessage, httpStatus) {
    super(rawMessage || code)
    this.name = 'ApiError'
    this.code = code
    this.rawMessage = rawMessage
    this.httpStatus = httpStatus
  }

  /**
   * 当前语言下该给用户看的提示。
   *
   * 后端只给错误码不给文案，翻译在这里完成——同一个后端因此能同时服务中文和日文用户，
   * 改文案也不需要重新部署后端。
   *
   * @returns {string}
   */
  get localizedMessage() {
    const key = `error.${this.code}`
    const translated = i18n.global.t(key)

    if (translated === key) {
      return this.rawMessage || i18n.global.t('common.unknownError')
    }

    // Surface Twilio/provider detail after the localized title.
    if (this.code === 'SMS_SEND_FAILED' && this.rawMessage) {
      return `${translated}（${this.rawMessage}）`
    }

    return translated
  }
}

/**
 * 认证失效时的回调。
 *
 * 用注册回调而不是让这个模块直接 import router，是为了避免
 * http → router → store → api → http 这条循环依赖。同时也让这一层不必知道
 * 「失效之后要做什么」——跳登录页是路由层的职责。
 *
 * @type {(() => void) | null}
 */
let unauthorizedHandler = null

/**
 * @param {() => void} handler
 */
export function setUnauthorizedHandler(handler) {
  unauthorizedHandler = handler
}

export const http = axios.create({
  // 相对路径。开发环境由 Vite 代理转发到后端，生产环境由 Nginx 转发，
  // 两边浏览器看到的都是同源请求，代码里不需要区分。
  baseURL: '/api',

  // 15 秒。移动网络在电梯、地下室里会很慢，但超过 15 秒用户早就以为卡死了，
  // 与其让他一直等，不如给个明确的失败提示让他重试。
  timeout: 15000,

  headers: {
    'Content-Type': 'application/json',
  },
})

/**
 * 请求拦截器：带上 token。
 */
http.interceptors.request.use((config) => {
  const token = getToken()
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }

  // 把当前语言告诉后端。目前后端只返回错误码不返回文案，用不到它，
  // 但将来后端要发短信、生成 PDF 对账单时需要知道用什么语言。
  config.headers['Accept-Language'] = i18n.global.locale.value

  return config
})

/**
 * 响应拦截器：拆掉统一响应体的外壳，并把失败转成 ApiError。
 *
 * 处理完之后，业务代码拿到的直接就是 data 字段的内容：
 *
 *   const user = await authApi.me()   // 而不是 res.data.data
 */
http.interceptors.response.use(
  (response) => {
    const body = response.data

    // 后端所有接口都返回 { success, data, code, message }。
    // 拿到别的结构说明请求根本没到后端——最常见的是开发时代理没配好，
    // 请求被 Vite 当成前端路由返回了 index.html。
    if (!body || typeof body.success !== 'boolean') {
      throw new ApiError('INTERNAL_ERROR', 'Unexpected response shape', response.status)
    }

    if (!body.success) {
      throw new ApiError(body.code, body.message, response.status)
    }

    return body.data
  },

  (error) => {
    // 请求压根没发出去或没收到响应：断网、DNS 挂了、跨域被拦、超时。
    if (!error.response) {
      const code = error.code === 'ECONNABORTED' ? 'TIMEOUT' : 'NETWORK_ERROR'
      return Promise.reject(new ApiError(code, error.message))
    }

    const status = error.response.status
    const body = error.response.data || {}
    const apiError = new ApiError(body.code || 'INTERNAL_ERROR', body.message, status)

    // 401 表示身份已经不成立了：token 过期、被篡改、或者账号已被停用。
    // 无论哪种，本地存的凭据都没用了，留着只会让每个后续请求都再失败一次。
    //
    // 403 不在此列：那是「你是谁我知道，但你不能干这个」，登录状态依然有效，
    // 清掉凭据反而会把用户莫名其妙踢出去。
    if (status === 401) {
      // 登录/注册失败是 401（AUTH_BAD_CREDENTIALS）。改密时旧密码不对以前也是 401。
      // 这两种都不能当成「会话过期」：否则用户会被踢去登录页，页面上看不到任何提示。
      const url = String(error.config?.url || '')
      const isCredentialAttempt =
        url.includes('/auth/login')
        || url.includes('/auth/register')
        || url.includes('/auth/change-password')
      if (!isCredentialAttempt) {
        clearAuthStorage()
        if (unauthorizedHandler) {
          unauthorizedHandler()
        }
      }
    }

    return Promise.reject(apiError)
  },
)
